import type { FormVersion } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import { isNil } from "lodash-es";
import { createEffect, on } from "solid-js";
import { trackStore } from "@solid-primitives/deep";
import type { SetStoreFunction } from "solid-js/store";
import { initialized } from "../utils/initializedUtil";
import ApiService from "./ApiService";
import { toFormVersionDto } from "../utils/mappers/formVersion";
import LegacyPlatformService from "./LegacyPlatformService";
import logger from "../utils/logger";
import FormId from "../state/FormId";
import FormVersionId from "../state/FormVersionId";
import type { QueryClient } from "@tanstack/solid-query";

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;

const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();

  createEffect(
    on(
      () => [
        trackStore(getFormVersionStore().fields),
        trackStore(getFormVersionStore().subforms),
        trackStore(getFormVersionStore().rules),
        trackStore(getFormVersionStore().integrations),
        trackStore(getFormVersionStore().variables),
        trackStore(getFormVersionStore().settings),
        trackStore(getFormVersionStore().theme),
      ],
      () => {
        getSetFormVersionStore()("lastUpdatedAt", () => new Date());
      },
      { defer: true },
    ),
  );
};

const save = async (queryClient: QueryClient) => {
  try {
    await ApiService.postFormVersion(queryClient, toFormVersionDto(getFormVersionStore()));
    getSetFormVersionStore()("lastUpdatedAt", () => undefined);
    LegacyPlatformService.notifySaved();
  } catch (e) {
    logger.error("Failed to save form version", e);
    throw e;
  }
};

const publish = async (queryClient: QueryClient) => {
  await save(queryClient);

  try {
    const formVersion = getFormVersionStore();
    const response = await ApiService.finalizeFormVersion(formVersion.id, formVersion.formId);
    await ApiService.publishFormVersion(queryClient, FormVersionId.from(response.id), FormId.from(response.formId));
    LegacyPlatformService.notifyPublished();
  } catch (e) {
    logger.error("Failed to publish form version", e);
    throw e;
  }
};

const FormVersionService = {
  isDraft: () => getFormVersionStore().meta.status === "DRAFT",
  hasUpdated: () => !isNil(getFormVersionStore().lastUpdatedAt),
  save,
  publish,
  init,
};

export default FormVersionService;
